import { useParams, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";

import { useEffect, useState } from "react";
import { FaTrophy } from "react-icons/fa";

export default function CompetitionDetail() {
  const { id } = useParams();
  const location = useLocation();

  const [competition, setCompetition] = useState(location.state?.comp || null);

  // Fetch JSON if not passed in state
  useEffect(() => {
    if (!competition) {
      fetch("/data/competitions.json")
        .then((res) => res.json())
        .then((data) => {
          const comp = data.find((c) => c.id === id);
          setCompetition(comp);
        })
        .catch((err) => console.error(err));
    }
  }, [competition, id]);

  if (!competition) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-600">Competition not found.</p>
        <Link
          to="/competitions"
          className="text-blue-600 underline hover:text-blue-800"
        >
          ← Back to Competitions
        </Link>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 25 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-3xl mx-auto"
    >
      {/* Back Button */}
      <Link
        to="/competitions"
        className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
      >
        ← Back to Competitions
      </Link>
      {/* Main Image */}
      <motion.img
        src={
          competition.image && competition.image.length > 0
            ? process.env.PUBLIC_URL + competition.image
            : process.env.PUBLIC_URL + "/images/default_banner.jpg"
        }
        alt={competition.title}
        className="w-full h-64 md:h-80 object-cover rounded-2xl shadow-md mb-6"
        initial={{ scale: 0.97 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.6 }}
      />

      {/* Title + Date */}
      <h2 className="text-3xl font-bold text-gray-900 mb-2">
        {competition.title}
      </h2>
      <p className="text-gray-500 mb-4">{competition.date}</p>
      <div className="h-1 w-100 mx-auto my-6 rounded-full bg-gradient-to-r from-blue-300 via-blue-400 to-blue-300"></div>

      {/* Details */}
      <p className="text-gray-700 leading-relaxed text-lg mb-10">
        {competition.details}
      </p>
      <div className="h-1 w-100 mx-auto my-6 rounded-full bg-gradient-to-r from-blue-300 via-blue-400 to-blue-300"></div>

      {/* Awards Section */}
      {competition.awards && competition.awards.length > 0 && (
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-gray-900 mb-4">
            🏆 Awards & Achievements
          </h3>
          <div className="space-y-4">
            {competition.awards.map((award, i) => {
              // Determine trophy color based on rank
              const getAwardColor = (title) => {
                const t = title.toLowerCase();

                if (
                  t.includes("champion") ||
                  t.includes("gold") ||
                  t.includes("first place")
                ) {
                  return "text-yellow-500"; // Gold
                }
                if (t.includes("second place") || t.includes("1st runner up")) {
                  return "text-gray-400"; // Silver
                }
                if (t.includes("third place") || t.includes("2nd runner up")) {
                  return "text-amber-700"; // Bronze
                }
                if (t.includes("fourth place") || t.includes("3rd runner up")) {
                  return "text-blue-400"; // Blue / Other
                }
                if (
                  t.includes("fifth place") ||
                  t.includes("4th runner-up") ||
                  t.includes("rookie award")
                ) {
                  return "text-green-400"; // Green / Other
                }

                return "text-blue-400"; // Default
              };

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center justify-between p-4 rounded-xl bg-white shadow-sm border border-gray-100 hover:shadow-md transition"
                >
                  {/* Award info */}
                  <div className="flex-1 pr-4">
                    <h4 className="font-semibold text-gray-900 text-lg flex items-center">
                      <FaTrophy
                        className={`mr-2 ${getAwardColor(award.title)}`}
                      />
                      {award.title}
                    </h4>
                    <p className="text-sm text-blue-600">{award.category}</p>
                    {award.description && (
                      <p className="text-gray-600 mt-1">{award.description}</p>
                    )}
                  </div>

                  {/* Award image */}
                  {award.image && (
                    <img
                      src={award.image}
                      alt={award.title}
                      className="w-16 h-16 object-cover rounded-lg border"
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
      <div className="h-1 w-100 mx-auto my-6 rounded-full bg-gradient-to-r from-blue-300 via-blue-400 to-blue-300"></div>

      {/* Media Gallery Section */}
      {competition.media && (
        <div className="mb-12">
          <h3 className="text-2xl font-semibold text-gray-900 mb-6">
            📸 Media Album
          </h3>

          {/* Hero Image */}
          {competition.media.hero && (
            <motion.img
              src={competition.media.hero}
              alt="Hero"
              className="w-full h-64 md:h-80 object-cover rounded-2xl mb-6 shadow-md"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            />
          )}

          {/* Grid Gallery */}
          <div className="grid gap-4 md:grid-cols-3">
            {competition.media?.gallery?.map((item, i) =>
              item.endsWith(".mp4") ? (
                <motion.video
                  key={i}
                  src={item}
                  controls
                  className="w-full h-48 object-cover rounded-xl shadow-sm"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                />
              ) : (
                <motion.img
                  key={i}
                  src={item}
                  alt={`Media ${i + 1}`}
                  className="w-full h-48 object-cover rounded-xl shadow-sm"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                />
              )
            ) ?? null}
          </div>
        </div>
      )}
    </motion.div>
  );
}
